import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { ICommonServiceProps } from "@api/types/globalService.types";
import { urlUtility } from "@repo/utilities/url.utility.ts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";
import { removeEmptyFields } from "@repo/utilities/object.utility.ts";

interface IPoliciesServiceProps extends ICommonServiceProps {
  showActiveOnly?: boolean;
  planId?: number | string;
  partnerId?: number | string;
}
export const useGetAllPoliciesService = ({
  enableQuery = true,
  page = "1",
  per_page = "15",
  showActiveOnly = true,
  filters,
  partnerId = "",
  planId = "",
}: IPoliciesServiceProps) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const currPartnerId = urlQueries["partner-id"] || partnerId;
  const currPlanId = urlQueries["plan-id"] || planId;
  const allPoliciesService = useQuery({
    queryKey: [
      ...CACHE_KEYS.POLICIES,
      page,
      debouncedQuery,
      per_page,
      showActiveOnly,
      currPartnerId,
      currPlanId,
      JSON.stringify(filters),
    ],
    queryFn: () => {
      const searchParams = {
        ...removeEmptyFields({
          "filter[name]": debouncedQuery,
          "filter[partner_id]": currPartnerId,
          "filter[plan_id]": currPlanId,
          ...filters,
        }),
        page,
        per_page,
        include: "product,plans,payment_modes,product.subSegment,partner",
        ...(showActiveOnly && { "filter[is_active]": true }),
      };
      return handleGlobalGetRequestQuery({
        url: URLS.POLICIES,
        searchParams: searchParams,
      });
    },
    enabled: enableQuery,
  });

  return {
    states: { searchQuery, setSearchQuery },
    service: { allPoliciesService },
  };
};

export const useGetSinglePolicyService = () => {
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const policyId = urlQueries["policy-id"] || "";

  const singlePolicyService = useQuery({
    queryKey: [...CACHE_KEYS.POLICIES, policyId],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.POLICIES + "/" + policyId,
        searchParams: {
          include: "product,combo,plans,accounts,features",
        },
      }),
    enabled: policyId ? true : false,
  });
  return { service: { singlePolicyService } };
};
